import { useEffect, useRef } from 'react';
import { useHilContext } from '@bizfirst/hil-react';
import { hilInboxApiClient } from '@flow-studio/api';

interface HilPendingLoaderProps {
  actorID: string | undefined;
}

/**
 * Loads pending HIL inbox items from the backend on mount and pushes them to the HIL engine.
 * Must be rendered as a child of HilProvider.
 * No-ops when actorID is absent or VITE_HIL_DUMMY_MODE is active.
 */
export function HilPendingLoader({ actorID }: HilPendingLoaderProps): null {
  const { pushToEngine } = useHilContext();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!actorID || loadedRef.current) return;

    const isDummy = !!(import.meta as any).env?.VITE_HIL_DUMMY_MODE;
    if (isDummy) return;

    loadedRef.current = true;

    (async () => {
      try {
        const itemsResp = await hilInboxApiClient.getPendingItems(actorID);
        if (!itemsResp.success || !itemsResp.data?.length) return;

        for (const item of itemsResp.data) {
          const logResp = await hilInboxApiClient.getEventLogBySession(item.EngageSessionID);
          const log = logResp.data?.[0] ?? null;
          const envelope = hilInboxApiClient.mapToV13Envelope(item, log);
          pushToEngine(envelope);
        }
      } catch (err) {
        console.error('[HilPendingLoader] Failed to load pending HIL items:', err);
      }
    })();
  }, [actorID, pushToEngine]);

  return null;
}
